
import { computed, defineComponent, type PropType } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import { useAppRouter } from '@/common/route/useAppRouter';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
type ButtonTheme = 'info' | 'success' | 'warning' | 'danger' | 'secondary' | 'primary' | 'dark' | 'light' | 'link';

export default defineComponent({
    name: 'app-button',
    components: { RouterLink },

    props: {
        icon: { type: String, default: '' },
        block: { type: String, default: '' },
        theme: { type: String as PropType<ButtonTheme>, default: 'info' },
        size: { type: String as PropType<ButtonSize>, default: 'md' },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        name: { type: String, default: '' },
        tooltip: { type: String, default: '' },
        to: { type: [String, Object] as PropType<RouteLocationRaw>, default: undefined },
        /**  @deprecated(01/10/2026) Use `to` instead */
        href: { type: [String, Object] as PropType<RouteLocationRaw>, default: undefined }
    },

    emits: ['click'],

    setup(props, { emit }) {
        const router = useAppRouter();

        const isDisabled = computed(() => props.loading || props.disabled);
        const navigationTarget = computed(() => props.to ?? props.href);
        const isLink = computed(() => navigationTarget.value !== undefined);

        const resolvedTo = computed(() => {
            const target = navigationTarget.value;
            return target === undefined ? undefined : router.withAppId(target);
        });

        const elementType = computed(() => (isLink.value ? RouterLink : 'button'));
        const elementProps = computed(() =>
            isLink.value ? { to: resolvedTo.value } : { type: 'button', disabled: isDisabled.value }
        );

        const buttonClasses = computed(() => [
            'btn',
            'btn-' + props.theme,
            'btn-' + props.size,
            { 'ml-1': props.size !== 'xs' },
            { disabled: isDisabled.value }
        ]);

        const handleClick = (event: MouseEvent) => {
            if (isDisabled.value) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            emit('click', event);
        };

        return { elementType, elementProps, buttonClasses, isDisabled, handleClick };
    }
});
